import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import jwtDecode from 'jwt-decode'
import { getGroupToken, isGroupMode } from 'lib/merchant-group'
import { decodePermissions } from 'lib/permissions/bitmap'
import { parseCookies } from 'nookies'

type Props = {
  children: React.ReactNode
}

type DecodedToken = {
  merchant_uuid: string
  merchant_user_uuid: string
  permissions: string // bitmap base64 das permissões
  capabilities: Record<string, unknown>
  exp: number
}

export type PermissionsContextType = {
  permissions: string[]
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
}

export const PermissionsContext = createContext({} as PermissionsContextType)

export function PermissionsProvider({ children }: Props) {
  const [permissions, setPermissions] = useState<string[]>([])

  const getPermissionsFromToken = useCallback((): string[] => {
    try {
      // SC-10999: na visão multi-empresas valem as permissões do token de
      // grupo, que são a interseção das permissões nas empresas do grupo. Assim
      // nenhuma tela agregada oferece uma ação que alguma empresa negaria.
      const { ['nextauth.token']: contextToken } = parseCookies()
      const token = isGroupMode() ? getGroupToken() : contextToken

      if (!token) {
        return []
      }

      const decodedData = jwtDecode<DecodedToken>(token)
      return decodePermissions(decodedData.permissions)
    } catch (error) {
      console.error('Error decoding permissions from token:', error)
      return []
    }
  }, [])

  // Atualiza permissões no mount e quando o token mudar
  useEffect(() => {
    setPermissions(getPermissionsFromToken())

    const handlePermissionsUpdated = () => {
      setPermissions(getPermissionsFromToken())
    }

    window.addEventListener('permissions-updated', handlePermissionsUpdated)

    return () => {
      window.removeEventListener(
        'permissions-updated',
        handlePermissionsUpdated
      )
    }
  }, [getPermissionsFromToken])

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission)
  }

  const hasAnyPermission = (perms: string[]): boolean => {
    return perms.some((perm) => permissions.includes(perm))
  }

  const hasAllPermissions = (perms: string[]): boolean => {
    return perms.every((perm) => permissions.includes(perm))
  }

  const value = useMemo(
    () => ({
      permissions,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions
    }),
    [permissions]
  )

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  )
}

export const usePermissionsContext = (): PermissionsContextType => {
  const context = useContext(PermissionsContext)

  if (!context || Object.keys(context).length === 0) {
    return {
      permissions: [],
      hasPermission: () => false,
      hasAnyPermission: () => false,
      hasAllPermissions: () => false
    }
  }

  return context
}
